import http from '../../shared/http'
import { localizedDocumentTypeLabel } from '../../shared/helpers'
import {
	ChangelogEnvOptions,
	ChangelogKindOptions,
	ChangelogRepoOptions,
	CompaniesOptions,
	DOCUMENT_TYPES,
	DocumentOptions,
	StateOptions,
	TApi,
	TicketTypeOptions,
	TLanguages,
	TRoles,
	TSupporterRoles,
	TSupporterStatus
} from '../../shared/const'

/*
 * Feldbausteine der Filterleiste.
 *
 * Damit dieselbe Einschraenkung in jeder Ansicht gleich heisst, gleich
 * aussieht und denselben Schluessel ans Backend schickt, steht jede Definition
 * genau einmal hier. Eine Ansicht setzt ihre Leiste aus diesen Bausteinen
 * zusammen (siehe Companies/index.jsx) statt eigene Felder zu bauen.
 *
 * Der `key` ist zugleich der Schluessel im `filter`-Objekt der API und in der
 * Adresszeile (`?f_school=…`) — er darf sich also nicht beilaeufig aendern.
 */

const byLabel = (a, b) => String(a.label).localeCompare(String(b.label))

export const stateField = t => ({
	key: 'state',
	label: t('state'),
	options: [...StateOptions].sort(byLabel)
})

/*
 * Mit `withJmc` kommt die Junior Mini Company als vierte Form dazu — nur in der
 * Unternehmensliste der Verwaltung, die sie auch liefert (#92). Die Lehrkraft-
 * Liste kennt sie nicht; dort hätte der Eintrag keine Wirkung.
 */
export const companyTypeField = (t, { withJmc = false } = {}) => ({
	key: 'companyType',
	label: t('Company type'),
	options: withJmc ? [...CompaniesOptions, { value: 'JMC', label: 'JMC' }] : CompaniesOptions
})

export const branchField = (t, branches, language) => ({
	key: 'branch',
	label: t('branch'),
	options: (branches || [])
		.map(branch => ({
			value: branch.value_EN,
			label: language === TLanguages.EN ? branch.value_EN : branch.value_DE
		}))
		.sort(byLabel)
})

/*
 * Schultyp. Die Liste kommt je nach Aufrufer als Objekte aus dem Store
 * (`{ value }`) oder als blanke Namen von einer Stammdaten-Schnittstelle —
 * beides meint dasselbe, deshalb nimmt der Baustein beides an, statt jede
 * Ansicht ihre eigene Umformung schreiben zu lassen.
 */
export const schoolTypeField = (t, schoolTypes) => ({
	key: 'schoolType',
	label: t('School Types'),
	options: (schoolTypes || [])
		.map(type => (typeof type === 'string' ? type : type.value))
		.map(value => ({ value, label: value }))
		.sort(byLabel)
})

export const companyStatusField = t => ({
	key: 'companyStatus',
	label: t('Company status'),
	options: [
		{ value: 'ACTIVATED', label: t('Activated') },
		{ value: 'NOT_ACTIVATED', label: t('Registered') },
		{ value: 'IS_NEED_REVIEW', label: t('To be checked') }
	]
})

export const schoolStatusField = t => ({
	key: 'schoolStatus',
	label: t('Status'),
	valueType: 'boolean',
	options: [
		{ value: true, label: t('Verified') },
		{ value: false, label: t('Not Verified') }
	]
})

export const roleField = t => ({
	key: 'role',
	label: t('User roles'),
	options: [
		{ value: TRoles.STUDENT, label: t('Student') },
		{ value: TRoles.TEACHER, label: t('Teacher') }
	]
})

export const verifiedTeacherField = t => ({
	key: 'verifiedTeacher',
	label: t('Teacher Status'),
	valueType: 'boolean',
	options: [
		{ value: true, label: t('Verified') },
		{ value: false, label: t('Not Verified') }
	]
})

export const emailConfirmedField = t => ({
	key: 'emailConfirmed',
	label: t('Email Status'),
	valueType: 'boolean',
	options: [
		{ value: true, label: t('Accepted') },
		{ value: false, label: t('Pending') }
	]
})

export const expertConfirmedField = t => ({
	key: 'expertConfirmed',
	label: t('Status'),
	valueType: 'boolean',
	options: [
		{ value: true, label: t('Verified') },
		{ value: false, label: t('Not Verified') }
	]
})

export const invoiceStatusField = t => ({
	key: 'invoiceStatus',
	label: t('Status'),
	options: [
		{ value: 'Paid', label: t('Paid') },
		{ value: 'Unpaid', label: t('Unpaid') },
		{ value: 'Overdue', label: t('Overdue') },
		{ value: 'Credit', label: t('Credit') },
		{ value: 'Cancelled', label: t('Cancelled') }
	]
})

export const parentalConfirmationField = t => ({
	key: 'reviewedParentalConfirmation',
	label: t('Parental Confirmation'),
	options: [
		{ value: 'SUBMITTED', label: t('Submitted') },
		{ value: 'OPEN', label: t('Open') },
		{ value: 'CHECKED', label: t('Checked') },
		{ value: 'REJECTED', label: t('Rejected') }
	]
})

/*
 * „Neu seit" — der Zeitraum, in dem der Datensatz angelegt wurde.
 *
 * Gebaut für die Kacheln der Startseite: Dort steht „+10 Neu", und wer darauf
 * klickt, will genau diese zehn Zeilen sehen. Ohne dieses Feld führte die Zahl
 * in die vollständige Liste mit 214 Lehrkräften — eine Zahl, die auf etwas
 * anderes zeigt, als sie zählt.
 *
 * In Tagen und nicht als Datumsbereich: Die Kachel meint „seit gestern", und
 * ein Feld, das man auch von Hand sinnvoll benutzen kann, braucht drei
 * Antworten und keinen Kalender. Der Wert ist die Zahl der Tage; das Backend
 * rechnet damit `createdAt >= jetzt - n Tage`.
 */
export const createdWithinField = t => ({
	key: 'createdWithin',
	label: t('Added'),
	valueType: 'number',
	options: [
		{ value: 1, label: t('Last 24 hours') },
		{ value: 7, label: t('Last 7 days') },
		{ value: 30, label: t('Last 30 days') }
	]
})

export const ceoField = t => ({
	key: 'isCEO',
	label: 'CEO',
	valueType: 'boolean',
	options: [
		{ value: true, label: t('CEO') },
		{ value: false, label: t('Not CEO') }
	]
})

/*
 * Dokumentart. Rechnungen sind in der Admin-Ansicht ausgenommen: Die haben eine
 * eigene Liste, in der Dokumentliste waeren sie doppelt.
 */
export const documentTypeField = (t, { withInvoice = true } = {}) => ({
	key: 'type',
	label: t('Document Type'),
	options: DocumentOptions.filter(option => withInvoice || option.value !== DOCUMENT_TYPES.INVOICE)
		.map(option => ({ value: option.value, label: localizedDocumentTypeLabel(option.value) }))
		.sort(byLabel)
})

// Schulen und Lehrkraefte sind zu viele fuer eine feste Liste: Die alte Leiste
// zeigte die ersten zehn und sonst nichts. Hier fragt jede Eingabe den Server,
// und beim Scrollen kommt die naechste Seite nach.
export const schoolField = (t, { isPublic = false } = {}) => ({
	key: 'school',
	label: t('school'),
	valueType: 'number',
	loadOptions: ({ search, offset, limit }) =>
		http
			.get(isPublic ? TApi.COMMON_GET_SCHOOLS : TApi.ADMIN_GET_SCHOOLS, {
				params: { limit, offset, order: 'name', direction: 'asc', search }
			})
			.then(({ data }) => ({
				options: data.rows.map(school => ({ value: school.id, label: school.name })),
				hasMore: offset + data.rows.length < data.count
			}))
})

/*
 * Unternehmen: gleiche Ueberlegung wie bei den Schulen — mehrere hundert
 * Eintraege, die niemand am Stueck holt.
 *
 * Lehrkraefte bekommen ihre betreuten Firmen zur Auswahl. Der Admin-Endpunkt
 * stuende ihnen nicht offen, und eine Auswahl, die nichts findet, waere
 * schlimmer als kein Feld.
 */
export const companyField = (t, { scope = 'admin' } = {}) => {
	const isTeacher = scope === 'teacher'

	return {
		key: 'company',
		label: t('Company'),
		valueType: 'number',
		loadOptions: ({ search, offset, limit }) =>
			http
				.get(isTeacher ? TApi.TEACHER_GET_ASSIGNED_COMPANIES : TApi.ADMIN_GET_COMPANIES, {
					params: { limit, offset, order: 'name', direction: 'asc', search, filter: '{}' }
				})
				.then(({ data }) => {
					const rows = isTeacher ? data.supervisedCompanies : data.allCompanies
					return {
						options: rows.map(company => ({ value: company.id, label: company.name })),
						hasMore: offset + rows.length < data.count
					}
				})
	}
}

/* ---------------------------------------------------------------------------
 * Unterstuetzer:innen
 * ------------------------------------------------------------------------- */

export const supporterRoleField = t => ({
	key: 'role',
	label: t('roles'),
	options: Object.values(TSupporterRoles).map(role => ({
		value: role,
		label: t(`supporterRole${role}`)
	}))
})

// Geloeschte Profile sind anonymisiert — sie gezielt zu suchen ergibt nichts.
export const supporterStatusField = t => ({
	key: 'availabilityStatus',
	label: t('status'),
	options: Object.values(TSupporterStatus)
		.filter(status => status !== TSupporterStatus.DELETED)
		.map(status => ({ value: status, label: t(`supporterStatus${status}`) }))
})

/*
 * Zielbranche der Unterstützer:innen. Seit 07.08.2026 dieselbe Liste, aus der
 * eine Junior Company ihre Branche wählt (globale Option `branch`) — vorher
 * standen hier ganz andere Wörter, und das Matching konnte nie greifen.
 *
 * Gespeichert wird die englische Fassung (`value_EN`), angezeigt die deutsche.
 * `branches` sind die Rohdaten aus dem Store; ohne sie bleibt der Wert selbst
 * als Beschriftung stehen, statt dass der Filter leer wirkt.
 */
export const industryField = (t, industries, branches, language) => ({
	key: 'industry',
	label: t('industries'),
	options: (industries || [])
		.map(name => {
			const match = (branches || []).find(b => b.value_EN === name || b.value_DE === name)
			if (!match) return { value: name, label: name }
			return { value: name, label: language === TLanguages.EN ? match.value_EN : match.value_DE }
		})
		.sort(byLabel)
})

/*
 * Kompetenzen kommen gruppiert vom Server. Fuer die Auswahl brauchen wir sie
 * flach — der Gruppenname bleibt als Vorspann stehen, sonst steht dieselbe
 * Kompetenz aus zwei Gruppen zweimal ununterscheidbar da.
 */
export const competenceField = (t, competenceGroups) => ({
	key: 'competence',
	label: t('competences'),
	options: (competenceGroups || []).flatMap(group =>
		(group.items || []).map(name => ({ value: name, label: `${group.name}: ${name}` }))
	)
})

/*
 * Unternehmen der Unterstützer:innen (Anforderung #185).
 *
 * Das Unternehmen ist dort Freitext, kein Verweis auf eine Firma — deshalb
 * nicht `companyField` (Junior Companies, Wert = Kennung), sondern die Namen,
 * die der Server aus dem Bestand sammelt (`lookups.companies`, schon getrimmt,
 * ohne Dubletten und deutsch sortiert). Verglichen wird serverseitig ohne
 * Rücksicht auf Groß-/Kleinschreibung.
 */
export const supporterCompanyField = (t, companies) => ({
	key: 'company',
	label: t('company'),
	options: (companies || []).map(name => ({ value: name, label: name }))
})

/*
 * „Nur passende" — der Ersatz für den aufgelösten Reiter „Passende
 * Expert:innen" (09.08.2026).
 *
 * Ein Feld mit genau einem Wert sieht nach zu wenig aus für ein Aufklappmenü.
 * Es steht trotzdem hier und nicht als eigener Schalter in der Ansicht: So
 * zählt es in der Filterzahl mit, steht in der Adresszeile und wird pro Browser
 * gemerkt — wie jede andere Einschränkung auch. Die Kurzwahl darüber
 * (`filters.setField`) ist derselbe Wert, keine zweite Einstellung.
 */
export const matchingField = t => ({
	key: 'matching',
	label: t('Fit'),
	options: [{ value: 'yes', label: t('onlyMatchingExperts') }]
})

/* ---------------------------------------------------------------------------
 * Veranstaltungen & Neuigkeiten
 *
 * Die Auswahlwerte entstehen aus dem geladenen Bestand und nicht aus den
 * Stammdaten: Ein Filter auf etwas, das keine Nachricht traegt, fuehrt nur zu
 * leeren Listen. Deshalb nehmen diese Bausteine ihre Optionen entgegen.
 * ------------------------------------------------------------------------- */

export const newsStatusField = t => ({
	key: 'status',
	label: t('Status'),
	options: [
		{ value: 'Draft', label: t('Draft') },
		{ value: 'Active', label: t('Active') },
		{ value: 'Planned', label: t('Planned') },
		{ value: 'Expired', label: t('Expired') }
	]
})

export const newsTypeField = t => ({
	key: 'newsType',
	label: t('Message type'),
	options: [
		{ value: 'EVENT', label: t('EVENT') },
		{ value: 'NEWS', label: t('NEWS') }
	]
})

export const newsStateField = (t, options) => ({
	key: 'states',
	label: t('state'),
	options: options || []
})

export const newsRolesField = (t, options) => ({
	key: 'roles',
	label: t('User roles'),
	options: options || []
})

export const newsSchoolTypeField = (t, options) => ({
	key: 'schoolType',
	label: t('School Types'),
	options: options || []
})

export const newsCompanyTypeField = (t, options) => ({
	key: 'companyTypes',
	label: t('Company type'),
	options: options || []
})

export const newsBranchField = (t, options) => ({
	key: 'companyBranch',
	label: t('branch'),
	options: options || []
})

export const newsAuthorField = (t, authors) => ({
	key: 'author',
	label: t('Author'),
	options: (authors || []).map(email => ({ value: email, label: email }))
})

/* ---------------------------------------------------------------------------
 * Kommunikation
 * ------------------------------------------------------------------------- */

export const targetGroupField = (t, options) => ({
	key: 'targetGroup',
	label: t('Target group'),
	options: options || []
})

/* ---------------------------------------------------------------------------
 * Entwicklungen und Auslieferungen
 *
 * Die Ansicht ist nur auf Deutsch — sie beschreibt die Abrechnungsbeilage.
 * ------------------------------------------------------------------------- */

// Eigener Wert fuer "noch keinem Projekt zugeordnet". Ohne ihn waere genau die
// Menge, die man abarbeiten will, nicht auffindbar.
export const CHANGELOG_NO_PROJECT = 'none'

export const changelogProjectField = projects => ({
	key: 'projectId',
	label: 'Projekt',
	options: [
		...(projects || []).map(project => ({ value: String(project.id), label: project.title })),
		{ value: CHANGELOG_NO_PROJECT, label: 'Ohne Projekt' }
	]
})

// Die „Art" ist seit 31.10.2026 die der Anforderungen (Fehler, Wunsch, …).
// Ob Entwicklung oder Auslieferung, heißt deshalb „Eintrag".
export const changelogKindField = () => ({
	key: 'kind',
	label: 'Eintrag',
	options: ChangelogKindOptions
})

// Art wie bei den Anforderungen — dieselben Wörter, damit eine Entwicklung
// und ihre Anforderung gleich eingeordnet heißen.
export const changelogTypeField = () => ({
	key: 'type',
	label: 'Art',
	options: [...TicketTypeOptions, { value: CHANGELOG_NO_PROJECT, label: 'Ohne Art' }]
})

// Zugehörige Anforderung. Die Anforderung verlinkt mit ?f_ticketId=<Nummer>
// hierher; „Mit"/„Ohne Anforderung" stehen vorn, gesucht wird über die Liste
// der Anforderungen (Nummer oder Titel).
export const changelogTicketField = () => ({
	key: 'ticketId',
	label: 'Anforderung',
	loadOptions: ({ search, offset, limit }) =>
		http
			.get(TApi.ADMIN_TICKETS, {
				params: { limit, offset, search, scope: 'all', order: 'id', direction: 'desc' },
				disableToast: true
			})
			.then(({ data }) => ({
				options: [
					...(offset === 0 && !search
						? [
								{ value: 'any', label: 'Mit Anforderung' },
								{ value: CHANGELOG_NO_PROJECT, label: 'Ohne Anforderung' }
						  ]
						: []),
					...(data.rows || []).map(ticket => ({
						value: String(ticket.id),
						label: `#${ticket.id} ${ticket.title}`
					}))
				],
				hasMore: offset + (data.rows || []).length < data.count
			}))
})

export const changelogRepoField = () => ({
	key: 'repo',
	label: 'Teil',
	options: ChangelogRepoOptions
})

export const changelogEnvField = () => ({
	key: 'environment',
	label: 'Umgebung',
	options: ChangelogEnvOptions
})

export const changelogCategoryField = categories => ({
	key: 'category',
	label: 'Einordnung',
	options: (categories || []).map(value => ({ value, label: value }))
})

export const changelogAreaField = areas => ({
	key: 'area',
	label: 'Bereich',
	options: (areas || []).map(value => ({ value, label: value }))
})

export const teacherField = t => ({
	key: 'teacher',
	label: t('Teacher'),
	valueType: 'number',
	loadOptions: ({ search, offset, limit }) =>
		http
			.get(TApi.ADMIN_GET_USERS, {
				params: { limit, offset, order: 'firstName', direction: 'asc', role: TRoles.TEACHER, search }
			})
			.then(({ data }) => ({
				options: data.rows.map(teacher => ({
					value: teacher.id,
					label: `${teacher.firstName} ${teacher.lastName}`
				})),
				hasMore: offset + data.rows.length < data.count
			}))
})

/*
 * Geschlecht — bisher nur in der Auswertung zum Skills4Retail-Konzept
 * gebraucht. Die Werte sind dieselben wie am Benutzerprofil (GENDER im
 * Backend); wer nichts hinterlegt hat, faellt aus jeder Auswahl heraus.
 */
export const genderField = t => ({
	key: 'gender',
	label: t('Gender'),
	options: [
		{ value: 'FEMALE', label: t('Female') },
		{ value: 'MALE', label: t('Male') },
		{ value: 'DIVERS', label: t('Divers') }
	]
})

/*
 * Die Stufen des Skills4Retail-Trichters. Sie kommen vom Server, damit
 * Beschriftung und Reihenfolge nicht an zwei Stellen gepflegt werden muessen —
 * dieselbe Liste beschriftet auch den Trichter im Ueberblick.
 */
export const conceptStageField = stages => ({
	key: 'stage',
	label: 'Stufe',
	options: (stages || []).map(stage => ({ value: stage.key, label: stage.label }))
})
